// ============================================================
// Login page. Requests a magic link; once it's clicked from the user's
// email (or pasted from the API's dev console), the API sets the cookie
// and redirects back here. The portal then reloads and DashboardProvider
// sees the session.
// ============================================================
import { useState, type FormEvent } from 'react';
import { createClient, ApiError } from '@risma/data-client';

interface LoginPageProps {
  apiUrl: string;
  /**
   * Lo slug del tenant di questo portale. In prod lo inietta il robot a build
   * time (VITE_RISMA_TENANT_SLUG); in dev ripiega sul tenant del seed locale.
   * Serve a chiedere il magic-link al tenant giusto.
   */
  tenantSlug: string;
}

type State =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent' }
  | { kind: 'error'; message: string };

export function LoginPage({ apiUrl, tenantSlug }: LoginPageProps) {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<State>({ kind: 'idle' });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;

    setState({ kind: 'sending' });
    try {
      const client = createClient({ baseUrl: apiUrl });
      await client.requestMagicLink({ email: trimmed, tenantSlug });
      setState({ kind: 'sent' });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `${err.status}: ${err.code}`
          : err instanceof Error
            ? err.message
            : 'unknown error';
      setState({ kind: 'error', message });
    }
  };

  return (
    <div className="login-shell">
      <div className="login-card">
        <div className="login-mono" aria-hidden="true">
          R<i>studio</i>
        </div>
        <h1 className="login-title">
          Accedi al <span className="serif">portale</span>.
        </h1>
        <p className="login-sub">
          Inserisci la tua email. Riceverai un link sicuro per accedere — niente password.
        </p>

        {state.kind === 'sent' ? (
          <div className="login-sent">
            <div className="eye" style={{ marginBottom: 6 }}>
              Email inviata
            </div>
            <p>
              Controlla la casella di <strong>{email}</strong>. Il link scade tra 15 minuti.
            </p>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ marginTop: 14 }}
              onClick={() => setState({ kind: 'idle' })}
            >
              Invia di nuovo
            </button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <div className="field">
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="elia@risma.studio"
                autoComplete="email"
                autoFocus
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              style={{ width: '100%', marginTop: 6 }}
              disabled={state.kind === 'sending'}
            >
              {state.kind === 'sending' ? 'Invio…' : 'Invia link'}
            </button>

            {state.kind === 'error' && (
              <div className="login-error" role="alert">
                Impossibile inviare il link ({state.message}).
              </div>
            )}
          </form>
        )}

        <div className="login-foot">
          <span>{tenantSlug}</span>
          <span style={{ color: 'var(--mute)' }}> · Risma <i>studio</i></span>
        </div>
      </div>
    </div>
  );
}
